import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  message,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  theme,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DiffOutlined,
  EditOutlined,
  MailOutlined,
  RollbackOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { IEmailTemplateSummary } from "../../interfaces/emailTemplate.interface";
import GeneralTableHeader from "../common/general-table-header/GeneralTableHeader";
import {
  discardEmailDraftThunk,
  getEmailTemplatesThunk,
  loadPendingStatesThunk,
  selectEmailTemplates,
  selectEmailTemplatesPending,
  selectEmailTemplatesStatus,
} from "./emailTemplatesSlice";
import { LOCALE_LABELS } from "./emailTemplatesHelpers";
import EmailTemplateEditor from "./components/EmailTemplateEditor";
import TemplateDiffDrawer from "./components/TemplateDiffDrawer";

const { Text } = Typography;

const isProd = process.env.REACT_APP_STAGE === "prod";

const EmailTemplates: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const templates = useAppSelector(selectEmailTemplates);
  const status = useAppSelector(selectEmailTemplatesStatus);
  const pending = useAppSelector(selectEmailTemplatesPending);

  const [search, setSearch] = useState("");
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [diffSlug, setDiffSlug] = useState<string | null>(null);

  useEffect(() => {
    dispatch(getEmailTemplatesThunk());
  }, [dispatch]);

  useEffect(() => {
    if (templates.length) {
      dispatch(loadPendingStatesThunk(templates.map((t) => t.slug)));
    }
  }, [dispatch, templates]);

  const closeEditor = () => {
    setEditingSlug(null);
    dispatch(getEmailTemplatesThunk());
  };

  const handleDiscard = async (slug: string) => {
    try {
      await dispatch(discardEmailDraftThunk({ slug })).unwrap();
      message.success("Draft discarded — reverted to published.");
    } catch {
      message.error("Failed to discard draft.");
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((t) => t.slug.toLowerCase().includes(term));
  }, [templates, search]);

  const columns: ColumnsType<IEmailTemplateSummary> = useMemo(
    () => [
      {
        title: "Template",
        dataIndex: "slug",
        key: "slug",
        render: (slug: string) => <Text strong>{slug}</Text>,
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) => {
          const isPending = pending[record.slug];
          if (isPending === undefined) return <Spin size="small" />;
          return isPending ? (
            <Tag color="orange">Pending draft</Tag>
          ) : (
            <Tag color="default">Published</Tag>
          );
        },
      },
      {
        title: "Languages",
        dataIndex: "locales",
        key: "locales",
        render: (locales: string[]) => (
          <Space size={[4, 4]} wrap>
            {locales.map((l) => (
              <Tag key={l}>{LOCALE_LABELS[l] ?? l}</Tag>
            ))}
          </Space>
        ),
      },
      {
        title: "Variables",
        dataIndex: "params",
        key: "params",
        render: (params: string[]) =>
          params.length ? (
            <Space size={[4, 4]} wrap>
              {params.map((p) => (
                <Tag key={p} color="processing">
                  {`{{${p}}}`}
                </Tag>
              ))}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "right",
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setEditingSlug(record.slug)}
            >
              Edit
            </Button>
            <Button
              size="small"
              icon={<DiffOutlined />}
              disabled={!pending[record.slug]}
              onClick={() => setDiffSlug(record.slug)}
            >
              Review
            </Button>
            <Popconfirm
              title="Discard draft changes?"
              description="Reverts the draft to the published content."
              okText="Discard"
              okButtonProps={{ danger: true }}
              disabled={!pending[record.slug]}
              onConfirm={() => handleDiscard(record.slug)}
            >
              <Button
                size="small"
                danger
                icon={<RollbackOutlined />}
                disabled={!pending[record.slug]}
              />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pending]
  );

  return (
    <div style={{ padding: token.paddingLG }}>
      <GeneralTableHeader
        title="Email Templates"
        avatarUrl={<MailOutlined />}
      />

      {!isProd && (
        <Alert
          style={{ marginTop: token.margin }}
          type="info"
          showIcon
          message="Non-production admin panel"
          description="You can make draft changes here only. This content is shared with the production email templates — the same data every environment reads. Publishing a template, or publishing a pending draft, can only be done from the production admin panel."
        />
      )}

      <Card style={{ marginTop: token.margin }}>
        <Space style={{ marginBottom: token.margin }} wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search templates..."
            style={{ width: 320 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Space>

        <Table
          rowKey="slug"
          loading={status === "loading"}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
        />
      </Card>

      <EmailTemplateEditor
        open={editingSlug !== null}
        slug={editingSlug}
        onClose={closeEditor}
      />

      <TemplateDiffDrawer
        open={diffSlug !== null}
        slug={diffSlug}
        onClose={() => setDiffSlug(null)}
        onDiscarded={() => setDiffSlug(null)}
      />
    </div>
  );
};

export default EmailTemplates;
